import { useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useClientId } from '@/features/value/hooks/useClientId';
import { useLijstStandStore, type Weergave } from '@/stores/lijstStandStore';
import { useStartadres } from '@/components/tabel';
import { useClientDashboard, useClientBrands } from '../hooks/useCompleteness';
import { ProductList } from '../components/ProductList';
import { ViewToggle, type ViewMode } from '../components/ViewToggle';
import { BrandGrid } from '../components/BrandGrid';
import type { Startfilter } from '@xxtract/tabelfilters';
import { gekozenMerk, merkNaam, PRODUCT_LIJST, PRODUCT_PAGINAFILTER, productenSleutels } from '../productLijst';

const LEGE_START: Startfilter = { filters: {}, sortering: [], gebruikt: [], geweigerd: [] };
const WEERGAVE_NAAR_MODUS: Record<Weergave, ViewMode> = { raster: 'grid', lijst: 'brand-list', producten: 'products' };
const MODUS_NAAR_WEERGAVE: Record<ViewMode, Weergave> = { grid: 'raster', 'brand-list': 'lijst', products: 'producten' };

/**
 * spec-tabelfilters story 9: één lezer voor binnenkomende links (tabel én paginafilters); daarna
 * staan filters, categorietab en weergave in de lijststand van deze klant, niet in het adres (CAP-14).
 */
export function ProductListPage() {
  const { t } = useTranslation();
  const clientId = useClientId();
  const sleutels = productenSleutels(clientId);
  const startlijsten = useMemo(
    () => [
      { lijst: PRODUCT_LIJST, standSleutel: sleutels.lijst },
      { lijst: PRODUCT_PAGINAFILTER, standSleutel: sleutels.pagina },
    ],
    [sleutels.lijst, sleutels.pagina],
  );
  const { start: gelezen, geweigerd, wisGeweigerd } = useStartadres(startlijsten);
  const metStart = Object.keys(gelezen).length > 0;
  // De stand na een link is precies de link: de stand die de link niet vult, gaat naar de standaard (§7).
  const start = metStart
    ? { [sleutels.lijst]: gelezen[sleutels.lijst] ?? LEGE_START, [sleutels.pagina]: gelezen[sleutels.pagina] ?? LEGE_START }
    : gelezen;

  const paginaStand = useLijstStandStore((s) => s.standen[sleutels.pagina]);
  const zet = useLijstStandStore((s) => s.zet);
  // Een link met een product- of paginafilter opent de producttabel; anders de bewaarde weergave (standaard raster).
  const weergave: Weergave = metStart ? 'producten' : (paginaStand?.weergave ?? 'raster');
  const lijstGelezen = Boolean(gelezen[sleutels.lijst]);
  const paginaGelezen = Boolean(gelezen[sleutels.pagina]);
  useLayoutEffect(() => {
    if (!metStart) return;
    zet(sleutels.pagina, paginaGelezen ? { weergave: 'producten' } : { weergave: 'producten', filters: {} });
    if (!lijstGelezen) zet(sleutels.lijst, { filters: {}, sortering: [], pagina: 1 });
  }, [metStart, lijstGelezen, paginaGelezen, sleutels.pagina, sleutels.lijst, zet]);
  const viewMode = WEERGAVE_NAAR_MODUS[weergave];
  // Vast merk (dashboardlink of -keuze): de merknaam is de kop (besluit Friso 27-09-2026).
  const vastMerk = gekozenMerk(metStart ? start[sleutels.pagina]?.filters : paginaStand?.filters);
  const setViewMode = (modus: ViewMode) => zet(sleutels.pagina, { weergave: MODUS_NAAR_WEERGAVE[modus] });
  const { data: dashboardData } = useClientDashboard(clientId);
  const categories = dashboardData?.data?.categories ?? [];

  const {
    data: brandsData,
    isLoading: brandsLoading,
    isError: brandsError,
    error: brandsErrorObj,
    refetch: brandsRefetch,
  } = useClientBrands(clientId);

  return (
    <div data-testid="product-list-page" className="space-y-5">
      {/* Page header with view toggle */}
      <div className="flex items-start justify-between">
        <div>
          <h1
            style={{
              fontSize: '1.25rem',
              fontWeight: 700,
              letterSpacing: '-0.02em',
              color: 'var(--foreground-heading)',
            }}
          >
            {vastMerk ? merkNaam(vastMerk) : t('products.title')}
          </h1>
          <p
            className="mt-[3px]"
            style={{ fontSize: '0.8125rem', color: 'var(--foreground-muted)' }}
          >
            {t('products.subtitle')}
          </p>
        </div>
        <ViewToggle activeView={viewMode} onViewChange={setViewMode} />
      </div>

      {/* Brand Grid View */}
      {viewMode === 'grid' && (
        <BrandGrid
          brands={brandsData?.data}
          isLoading={brandsLoading}
          isError={brandsError}
          error={brandsErrorObj}
          onRetry={() => brandsRefetch()}
          layout="grid"
        />
      )}

      {/* Brand List View */}
      {viewMode === 'brand-list' && (
        <BrandGrid
          brands={brandsData?.data}
          isLoading={brandsLoading}
          isError={brandsError}
          error={brandsErrorObj}
          onRetry={() => brandsRefetch()}
          layout="list"
        />
      )}

      {/* Product Table View */}
      {viewMode === 'products' && (
        <ProductList categories={categories} clientId={clientId} key={clientId} linkGeweigerd={geweigerd} onLinkGewist={wisGeweigerd} start={start} />
      )}
    </div>
  );
}
